// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: who has read up to a message, as their pictures sitting at the end of
// the message the way "(edited)" does. In a DM only your own messages carry
// them; in group DMs and server channels every message can, so the faces show
// who has caught up with the conversation. Pictures only appear once a message
// has actually been seen — there is no sending or delivered state here, so an
// absent avatar means "not read yet" and nothing else.
//
// Each reader appears exactly once in the conversation, on the newest message
// they have read (see SeenAnchors), so their picture walks down the channel as
// they catch up rather than repeating on the whole backlog. Clicking the
// pictures lists everyone there with when they read it.

import Authentication from '@app/features/auth/state/Authentication';
import {useMessageViewContext} from '@app/features/channel/components/MessageViewContext';
import styles from '@app/features/read_state/components/MessageSeenBy.module.css';
import ReadReceipts, {isReadReceiptChannelType, seenAnchorModeFor} from '@app/features/read_state/state/ReadReceipts';
import {AvatarStack} from '@app/features/ui/avatars/AvatarStack';
import {Avatar} from '@app/features/ui/components/Avatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import {getRelativeDateString} from '@app/features/user/utils/DateFormatting';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {MessageTypes} from '@fluxer/constants/src/ChannelConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const SEEN_BY_DESCRIPTOR = msg({
	message: 'Seen by {names}',
	comment: 'Label on the reader pictures under a chat message. {names} lists who has read up to it.',
});
const SEEN_BY_TITLE_DESCRIPTOR = msg({
	message: 'Seen by',
	comment: 'Title of the list that opens from the reader pictures under a chat message.',
});

const AVATAR_SIZE = 14;
const LIST_AVATAR_SIZE = 24;
const MAX_VISIBLE = 3;
/** Names spelled out in the hover label before the rest become "+N". */
const MAX_NAMED = 3;

const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

const SeenByList = observer(({readers, channelId}: {readers: ReadonlyArray<User>; channelId: string}) => {
	const {i18n} = useLingui();
	const title = i18n._(SEEN_BY_TITLE_DESCRIPTOR);
	const rows = readers
		.map((user) => ({user, readAt: ReadReceipts.getReadAt(channelId, user.id)}))
		.sort((a, b) => (b.readAt ?? 0) - (a.readAt ?? 0));
	return (
		<div className={styles.list} role="dialog" aria-label={title} data-flx="read-state.message-seen-by.list">
			<div className={styles.listTitle}>{title}</div>
			<ul className={styles.listRows}>
				{rows.map(({user, readAt}) => (
					<li key={user.id} className={styles.listRow} data-flx="read-state.message-seen-by.list-row">
						<Avatar user={user} size={LIST_AVATAR_SIZE} data-flx="read-state.message-seen-by.list-avatar" />
						<span className={styles.listName}>{NicknameUtils.getNickname(user, null, channelId)}</span>
						{readAt != null && <span className={styles.listTime}>{getRelativeDateString(readAt, i18n)}</span>}
					</li>
				))}
			</ul>
		</div>
	);
});

/**
 * The readers' pictures as one button: hover names them, click lists them with when they read.
 * Shared by the chat (MessageSeenBy) and the Home pop-up, which draws its own bubbles.
 */
export const SeenByReaders = observer(
	({readers, channelId, className}: {readers: ReadonlyArray<User>; channelId: string; className?: string}) => {
		const {i18n} = useLingui();
		const named = readers.slice(0, MAX_NAMED).map((user) => NicknameUtils.getNickname(user, null, channelId));
		const names = readers.length > MAX_NAMED ? `${named.join(', ')} +${readers.length - MAX_NAMED}` : named.join(', ');
		const label = i18n._(SEEN_BY_DESCRIPTOR, {names});
		return (
			<span
				className={clsx(styles.seenBy, className)}
				role="group"
				aria-label={label}
				onClick={stopPropagation}
				onKeyDown={stopPropagation}
				data-flx="read-state.message-seen-by"
			>
				<Popout
					position="top-end"
					animationType="none"
					offsetMainAxis={6}
					tooltip={label}
					render={() => <SeenByList readers={readers} channelId={channelId} />}
					data-flx="read-state.message-seen-by.popout"
				>
					<FocusRing offset={-2} data-flx="read-state.message-seen-by.focus-ring">
						<button
							type="button"
							className={styles.trigger}
							aria-label={label}
							data-flx="read-state.message-seen-by.trigger"
						>
							<AvatarStack
								users={readers}
								size={AVATAR_SIZE}
								maxVisible={MAX_VISIBLE}
								channelId={channelId}
								enableProfileModal={false}
								showTooltips={false}
								data-flx="read-state.message-seen-by.avatar-stack"
							/>
						</button>
					</FocusRing>
				</Popout>
			</span>
		);
	},
);

export const MessageSeenBy = observer(() => {
	const {channel, message, previewContext} = useMessageViewContext();
	const currentUserId = Authentication.currentUserId ?? null;
	const mode = seenAnchorModeFor(channel.type);
	const isOwnMessage = currentUserId != null && message.author.id === currentUserId;
	const isEligible =
		isReadReceiptChannelType(channel.type) &&
		(mode === 'all' || isOwnMessage) &&
		!previewContext &&
		message.type !== MessageTypes.CLIENT_SYSTEM;
	// Read the store unconditionally: hooks aside, an observer that bails before
	// touching it would not re-render when the receipt arrives.
	const readerIds = ReadReceipts.seenAnchors(channel.id, currentUserId, mode).get(message.id);
	if (!isEligible || !readerIds || readerIds.length === 0) return null;
	const readers = readerIds.map((userId) => Users.getUser(userId)).filter((user): user is User => user != null);
	if (readers.length === 0) return null;
	return <SeenByReaders readers={readers} channelId={channel.id} />;
});
